import * as React from "react";
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	FormHelperText,
	FormLabel,
	IconButton,
	InputLabel,
	OutlinedInput,
	Radio,
	RadioGroup,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { CaretDown } from "@phosphor-icons/react/dist/ssr/CaretDown";
import { PaperclipIcon } from "@phosphor-icons/react/dist/ssr/Paperclip";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { TrashIcon } from "@phosphor-icons/react/dist/ssr/Trash";
import { XIcon } from "@phosphor-icons/react/dist/ssr/X";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

import { BriefDocumentKind, useSubmitCampaignBrief } from "@/hooks/api/use-campaign-briefs";
import { useAuth } from "@/components/core/auth-provider";
import { CustomDatePicker } from "@/components/customDatePicker/custom-date-picker";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";
import { FilterOption } from "@/components/customTable/types";
import { FilterFetchSelect } from "@/components/fetch-select";
import { SchedulePicker } from "@/components/schedule/schedule-picker";

import { DOCUMENTS, formatBytes, LANGUAGES, REGIONS } from "./constants";
import {
	BriefFormErrors,
	BriefFormValues,
	emptyBriefForm,
	filesToUpload,
	toBriefPayload,
	validateBriefForm,
} from "./form";
import { RateCardSection } from "./rate-card-section";

interface NewRequestDialogProps {
	open: boolean;
	onClose: () => void;
}

/**
 * "New campaign request": the client says what they want run, attaches the files, and it goes to
 * us. Saving and uploading happen on one click; if anything fails, the saved draft is kept and
 * the next click carries on from there rather than starting a second request.
 */
export function NewRequestDialog({ open, onClose }: NewRequestDialogProps): React.JSX.Element {
	const navigate = useNavigate();
	const { user, account } = useAuth();
	const submit = useSubmitCampaignBrief();

	const [values, setValues] = React.useState<BriefFormValues>(emptyBriefForm);
	const [errors, setErrors] = React.useState<BriefFormErrors>({});
	const [draftId, setDraftId] = React.useState<string | null>(null);
	const [uploaded, setUploaded] = React.useState<Partial<Record<BriefDocumentKind, File>>>({});
	const [step, setStep] = React.useState<string | null>(null);

	const set = <K extends keyof BriefFormValues>(key: K, value: BriefFormValues[K]) => {
		setValues((current) => ({ ...current, [key]: value }));
		setErrors(({ [key]: _, ...rest }) => rest);
	};

	const setFile = (kind: BriefDocumentKind, file: File | null) => {
		setValues((current) => {
			const files = { ...current.files };
			if (file) files[kind] = file;
			else delete files[kind];
			return { ...current, files };
		});
		setErrors(({ [kind]: _, ...rest }) => rest);
	};

	const handleClose = () => {
		if (submit.isPending) return;
		onClose();
		setTimeout(() => {
			setValues(emptyBriefForm());
			setErrors({});
			setDraftId(null);
			setUploaded({});
			setStep(null);
		}, 300);
	};

	const handleSubmit = async () => {
		const found = validateBriefForm(values, dayjs().format("YYYY-MM-DD"));
		setErrors(found);
		if (Object.keys(found).length > 0) return;

		try {
			const brief = await submit.mutateAsync({
				draftId,
				payload: toBriefPayload(values),
				files: filesToUpload(values),
				uploaded,
				onDraft: setDraftId,
				onUploaded: (kind, file) => setUploaded((current) => ({ ...current, [kind]: file })),
				onStep: setStep,
			});
			handleClose();
			setTimeout(() => navigate(`/campaign-briefs/${brief.id}`), 300);
		} catch {
			// The toast says what went wrong; the draft and anything uploaded are kept for the retry.
			setStep(null);
		}
	};

	const errorFor = (key: string) => (errors[key] ? <CustomFormHelperError text={errors[key]!} /> : null);

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			sx={{ "& .MuiDialog-container": { "& .MuiPaper-root": { width: "100%", maxWidth: "880px" } } }}
		>
			<DialogTitle>
				New campaign request
				<IconButton color="inherit" onClick={handleClose} sx={{ position: "absolute", right: 8, top: 8 }}>
					<XIcon width={20} height={20} />
				</IconButton>
				<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
					We’ll review it and get back to you.
					{user?.name && account?.name ? ` Submitted as ${user.name} · ${account.name}.` : ""}
				</Typography>
			</DialogTitle>

			<DialogContent sx={{ pt: "8px !important" }}>
				<Stack spacing={3}>
					<FormControl error={Boolean(errors.title)} fullWidth required>
						<InputLabel>Campaign name</InputLabel>
						<OutlinedInput
							value={values.title}
							onChange={(event) => set("title", event.target.value)}
							placeholder="Q4 renewals"
						/>
						{errorFor("title")}
					</FormControl>

					<Stack spacing={1.5}>
						<FormControl error={Boolean(errors.dialDate)} fullWidth>
							<CustomDatePicker
								label="First dialling date"
								value={values.dialDate}
								minDate={dayjs()}
								setValue={(date) => set("dialDate", date)}
								error={Boolean(errors.dialDate)}
							/>
							{errorFor("dialDate")}
						</FormControl>
						{values.redialDates.map((date, index) => (
							<Box key={index}>
								<Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
									<CustomDatePicker
										label={`Redial ${index + 1} date`}
										value={date || null}
										minDate={values.dialDate ? dayjs(values.dialDate).add(1, "day") : dayjs()}
										setValue={(picked) =>
											set(
												"redialDates",
												values.redialDates.map((current, at) => (at === index ? (picked ?? "") : current))
											)
										}
										error={Boolean(errors[`redials.${index}`])}
									/>
									<IconButton
										aria-label={`Remove redial ${index + 1}`}
										onClick={() => {
											set(
												"redialDates",
												values.redialDates.filter((_, at) => at !== index)
											);
											setErrors({});
										}}
									>
										<TrashIcon />
									</IconButton>
								</Stack>
								{errorFor(`redials.${index}`)}
							</Box>
						))}
						<Box>
							<Button
								size="small"
								startIcon={<PlusIcon />}
								onClick={() => set("redialDates", [...values.redialDates, ""])}
							>
								Add redial date
							</Button>
						</Box>
					</Stack>

					<Stack spacing={1}>
						<Typography variant="subtitle2">Dialling hours</Typography>
						<FormHelperText sx={{ mx: 0, mt: 0 }}>
							When we may call on each weekday, for every dialling date. Leave gaps for breaks, e.g. 09:00–12:00 and
							13:00–17:00. Local time in each region: 09:00 means 09:00 in Singapore and 09:00 in Jakarta.
						</FormHelperText>
						<Box
							sx={{
								p: 2,
								border: "1px solid",
								borderColor: errors.schedule ? "error.main" : "var(--mui-palette-divider)",
								borderRadius: 1,
							}}
						>
							<SchedulePicker
								value={values.schedule}
								onChange={(slots) => {
									set("schedule", slots);
									setErrors(({ dialDate: _, ...rest }) =>
										Object.fromEntries(Object.entries(rest).filter(([key]) => !key.startsWith("redials.")))
									);
								}}
								showSummary
							/>
						</Box>
						{errorFor("schedule")}
					</Stack>

					<FormControl error={Boolean(errors.agentChoice || errors.agentId)}>
						<FormLabel>Agent</FormLabel>
						<RadioGroup
							row
							value={values.agentChoice ?? ""}
							onChange={(event) => set("agentChoice", event.target.value as BriefFormValues["agentChoice"])}
						>
							<FormControlLabel value="existing" control={<Radio />} label="Existing agent" />
							<FormControlLabel value="new" control={<Radio />} label="New agent" />
						</RadioGroup>
						{errorFor("agentChoice")}
						{values.agentChoice === "existing" ? (
							<Box sx={{ mt: 1 }}>
								<FilterFetchSelect
									label="Which agent?"
									showInputLabel
									value={{ label: values.agentName, value: values.agentId } as FilterOption}
									placeholder="Select one of your agents"
									onChange={(selected: FilterOption | string) => {
										const option = typeof selected === "string" ? { value: selected, label: selected } : selected;
										setValues((current) => ({
											...current,
											agentId: (option.value as string) || "",
											agentName: option.label || "",
										}));
										setErrors(({ agentId: _, ...rest }) => rest);
									}}
									getOptionsUrl="/campaign-briefs/agents"
									fetchLabel="name"
									fetchValue="id"
								/>
								{errorFor("agentId")}
							</Box>
						) : null}
						{values.agentChoice === "new" ? (
							<FormHelperText sx={{ mx: 0 }}>We’ll build it from the call flow you attach below.</FormHelperText>
						) : null}
					</FormControl>

					<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
						<FormControl error={Boolean(errors.regions)} fullWidth>
							<Autocomplete
								multiple
								disableCloseOnSelect
								options={REGIONS}
								popupIcon={<CaretDown size={18} />}
								getOptionLabel={(option) => `${option.label} (${option.clock})`}
								isOptionEqualToValue={(option, value) => option.value === value.value}
								value={REGIONS.filter(({ value }) => values.regions.includes(value))}
								onChange={(_, selected) =>
									set(
										"regions",
										selected.map(({ value }) => value)
									)
								}
								slotProps={{ chip: { color: "primary", size: "small" } }}
								renderInput={(params) => <TextField {...params} label="Regions" error={Boolean(errors.regions)} />}
							/>
							{errorFor("regions")}
						</FormControl>
						<FormControl error={Boolean(errors.languages)} fullWidth>
							<Autocomplete
								multiple
								disableCloseOnSelect
								options={LANGUAGES}
								popupIcon={<CaretDown size={18} />}
								getOptionLabel={(option) => option.label}
								isOptionEqualToValue={(option, value) => option.value === value.value}
								value={LANGUAGES.filter(({ value }) => values.languages.includes(value))}
								onChange={(_, selected) =>
									set(
										"languages",
										selected.map(({ value }) => value)
									)
								}
								slotProps={{ chip: { color: "primary", size: "small" } }}
								renderInput={(params) => <TextField {...params} label="Languages" error={Boolean(errors.languages)} />}
							/>
							{errorFor("languages")}
						</FormControl>
					</Stack>

					<Stack spacing={0} sx={{ border: "1px solid var(--mui-palette-divider)", borderRadius: 1 }}>
						{values.agentChoice === "new" ? (
							<>
								<FileRow
									kind="call_flow"
									file={values.files.call_flow}
									onChange={(file) => setFile("call_flow", file)}
									error={errors.call_flow}
								/>
								<Divider />
							</>
						) : null}
						<FileRow
							kind="lead_list"
							file={values.files.lead_list}
							onChange={(file) => setFile("lead_list", file)}
							error={errors.lead_list}
						/>
						<Divider />
						<FileRow
							kind="po"
							optional
							file={values.files.po}
							onChange={(file) => setFile("po", file)}
							error={errors.po}
							required={values.poRequired}
							onRequiredChange={(required) => set("poRequired", required)}
							requiredError={errors.poRequired}
						/>
						<Divider />
						<FileRow
							kind="sow"
							optional
							file={values.files.sow}
							onChange={(file) => setFile("sow", file)}
							error={errors.sow}
							required={values.sowRequired}
							onRequiredChange={(required) => set("sowRequired", required)}
							requiredError={errors.sowRequired}
						/>
					</Stack>

					<RateCardSection value={values.addOns} onChange={(codes) => set("addOns", codes)} />

					<TextField
						label="Notes"
						multiline
						minRows={3}
						value={values.notes}
						onChange={(event) => set("notes", event.target.value)}
						placeholder="Anything we should know: blackout dates, tone, who to escalate to"
					/>

					{draftId && !submit.isPending ? (
						<Alert severity="info">Your request is saved as a draft. Submit again to finish.</Alert>
					) : null}
				</Stack>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 3 }}>
				{step ? (
					<Typography variant="body2" color="text.secondary" sx={{ mr: "auto" }}>
						{step}…
					</Typography>
				) : null}
				<Button color="secondary" onClick={handleClose} disabled={submit.isPending}>
					Cancel
				</Button>
				<Button variant="contained" onClick={handleSubmit} disabled={submit.isPending}>
					Submit request
				</Button>
			</DialogActions>
		</Dialog>
	);
}

interface FileRowProps {
	kind: BriefDocumentKind;
	file: File | undefined;
	onChange: (file: File | null) => void;
	error?: string;
	optional?: boolean;
	/** For the PO and SOW: the client's answer to "Required?". */
	required?: boolean | null;
	onRequiredChange?: (required: boolean) => void;
	requiredError?: string;
}

function FileRow({
	kind,
	file,
	onChange,
	error,
	optional,
	required,
	onRequiredChange,
	requiredError,
}: FileRowProps): React.JSX.Element {
	const input = React.useRef<HTMLInputElement>(null);
	const { label, accept, hint } = DOCUMENTS[kind];

	return (
		<Box sx={{ px: 2, py: 1.5 }}>
			<Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ alignItems: { sm: "center" } }}>
				<Box sx={{ flex: "1 1 auto" }}>
					<Typography variant="subtitle2">
						{label}
						{optional ? (
							<Typography component="span" variant="caption" color="text.secondary">
								{" "}
								(optional)
							</Typography>
						) : null}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{hint}
					</Typography>
				</Box>

				{onRequiredChange ? (
					<FormControl error={Boolean(requiredError)} sx={{ flexDirection: "row", alignItems: "center", gap: 1 }}>
						<FormLabel sx={{ fontSize: 14 }}>Required?</FormLabel>
						<RadioGroup
							row
							value={required === null || required === undefined ? "" : String(required)}
							onChange={(event) => onRequiredChange(event.target.value === "true")}
						>
							<FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
							<FormControlLabel value="false" control={<Radio size="small" />} label="No" />
						</RadioGroup>
					</FormControl>
				) : null}

				{file ? (
					<Stack direction="row" spacing={0.5} sx={{ alignItems: "center", minWidth: 0 }}>
						<PaperclipIcon />
						<Typography variant="body2" noWrap sx={{ maxWidth: 180 }} title={file.name}>
							{file.name}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{formatBytes(file.size)}
						</Typography>
						<IconButton size="small" aria-label={`Remove ${label}`} onClick={() => onChange(null)}>
							<XIcon />
						</IconButton>
					</Stack>
				) : (
					<Button size="small" variant="outlined" onClick={() => input.current?.click()}>
						Choose file
					</Button>
				)}
				<input
					ref={input}
					type="file"
					hidden
					accept={accept}
					onChange={(event) => {
						onChange(event.target.files?.[0] ?? null);
						event.target.value = "";
					}}
				/>
			</Stack>
			{requiredError ? <CustomFormHelperError text={requiredError} /> : null}
			{error ? <CustomFormHelperError text={error} /> : null}
		</Box>
	);
}
